/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    Configuration,
    ChangeNames,
    ChangeSet,
    ChangeTypes,
    DocumentCollection,
    FormModel,
    OperationState,
    unwrap,
} from "../../clients/FirebaseClient/types";
import { defaultFirebaseClient as firebaseClient } from "../../clients/FirebaseClient";
import { defaultClient, InitialConfiguration } from "../../clients/ConfigurationClient";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { AppThunk, RootDispatch, ScreenActions } from "..";
import { getMimeType } from "../../utils/filetypes";
import { v4 as uuid } from "uuid";
import moment from "moment";
import { AuthActions } from "./authSlice";
import produce from "immer";
import {
    defaultAddressClient as addressClient,
    PartnerResponse,
} from "../../clients/AddressClient";
import { RootState } from "../store";
import { UploadTask } from "firebase/storage";

export type ConfigurationState = {
    currentConfigurationId?: string;
    initialized: boolean;
    creating?: boolean;
    configurations: {
        [key: string]: Configuration;
    };
    configurationToRestore?: Configuration;
    productPartners?: PartnerResponse["partners"];
};

export const initialConfigurationsState: ConfigurationState = {
    configurations: {},
    initialized: false,
    creating: false,
};

export const newConfiguration: Configuration = {
    id: "new",
    complete: false,
    createdAt: moment().unix(),
    documents: {},
    state: {
        buildingAddress: {
            default: true,
            relevant: true,
            state: {
                type: "postal",
                city: {
                    value: "",
                    error: null,
                },
                houseNumber: {
                    value: "",
                    error: null,
                },
                street: {
                    value: "",
                    error: null,
                },
                zipCode: {
                    value: "",
                    error: null,
                },
            },
        },
        productClasses: {
            default: true,
            relevant: true,
            state: {
                value: { gas: false, power: false },
            },
        },
    },
};

const subscribeConfigurations = (): AppThunk => {
    return async (_dispatch, getState): Promise<void> => {
        const uid = getState().auth.uid;
        if (!uid) {
            console.error("cannot subscribe to configuration of unknown user!");
            return;
        }
        return firebaseClient.subscribeConfigurations(uid);
    };
};

const subscribeStatic = (): AppThunk => {
    return async (): Promise<void> => {
        return firebaseClient.subscribeStatic();
    };
};

const initialize = createAsyncThunk<
    string,
    InitialConfiguration,
    { dispatch: RootDispatch; rejectValue: AxiosError }
>("INITIALIZE_CONFIGURATION", async (conf) => {
    const confId = await defaultClient.create(conf);
    return confId;
});

const initializeOperation =
    (address: InitialConfiguration) =>
    async (dispatch: RootDispatch, getState: () => RootState) => {
        const uid = getState().auth.uid;

        if (!uid) return { status: OperationState.UNKNOWN_ERROR };
        try {
            dispatch(ScreenActions.setActivity());
            const operationState = await firebaseClient.doWithOperation(
                uid,
                async (): Promise<string | void> => {
                    try {
                        const confId = await dispatch(initialize(address));
                        if (typeof confId.payload === "string") {
                            return confId.payload;
                        } else if (confId.payload?.isAxiosError) {
                            console.error("request failed:", confId.payload.response?.data);
                        }
                    } catch (e) {
                        if (e.response?.data?.message) {
                            console.error("request failed:", e.response.data.message);
                        } else {
                            console.error("request failed:", e);
                        }
                    }
                },
            );

            return operationState;
        } finally {
            dispatch(ScreenActions.finishActivity());
        }
    };

const change = createAsyncThunk<
    void,
    { confId: string; conf: ChangeSet<keyof ChangeTypes> },
    { rejectValue: AxiosError }
>("CHANGE_CONFIGURATION", async (confUpdate) => await defaultClient.update(confUpdate));

const order = createAsyncThunk<string, string, { rejectValue: AxiosError }>(
    "ORDER_CONFIGURATION",
    async (confId: string) => await defaultClient.order(confId),
);

const remove = createAsyncThunk<
    void,
    { confId: string } | { orderId: string },
    { rejectValue: AxiosError }
>("CONFIGURATION_DELETE", async (params) => await defaultClient.remove(params));

const requestPartner = (values: FormModel<ChangeNames.BUILDING_ADDRESS>) => {
    return async (dispatch: RootDispatch): Promise<PartnerResponse | null> => {
        try {
            dispatch(ScreenActions.setActivity());
            if (values.buildingAddress.type === "postal") {
                console.log("checking products for address", unwrap(values.buildingAddress));
                const response = await addressClient.requestPartners(
                    values.buildingAddress.zipCode.value,
                    values.buildingAddress.city.value,
                    values.buildingAddress.street?.value || "",
                    values.buildingAddress.houseNumber?.value || "",
                );
                if (!response || response?.corrected) {
                    return response;
                }
                dispatch(ConfigurationActions.setBuildingAddress(values.buildingAddress));
                dispatch(ConfigurationActions.productPartners(response.partners));
                return response;
            } else {
                dispatch(ConfigurationActions.setBuildingAddress(values.buildingAddress));
                dispatch(
                    ConfigurationActions.productPartners({
                        power: {
                            foreignProvider: false,
                        },
                        gas: {
                            foreignProvider: false,
                        },
                    }),
                );
                return {
                    corrected: false,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any;
            }
        } finally {
            dispatch(ScreenActions.finishActivity());
        }
    };
};

function documentRemove(path: string) {
    return async (): Promise<void> => {
        await firebaseClient.removeFile(path);
    };
}

let uploadTask: UploadTask | undefined = undefined;
let staledTimeoutId: number | undefined = undefined;

function documentCancelUpload() {
    return async (): Promise<void> => {
        if (uploadTask) {
            uploadTask.cancel();
            uploadTask = undefined;
        }
        if (staledTimeoutId) {
            clearTimeout(staledTimeoutId);
            staledTimeoutId = undefined;
        }
    };
}

function documentUpload(uid: string, classification: string, configurationId: string, file: File) {
    return async (dispatch: RootDispatch): Promise<void> => {
        const size = file.size;
        const userFilename = file.name;
        const mimeType = await getMimeType(file);

        const filename = uuid();

        const progressListener = (x: number): unknown => {
            if (staledTimeoutId && x > 0) {
                clearTimeout(staledTimeoutId);
                staledTimeoutId = undefined;
            }
            return dispatch(
                configurationSlice.actions.documentUploadProgress({
                    classification,
                    progress: x,
                }),
            );
        };
        const errorListener = (e: Error): void => {
            dispatch(
                configurationSlice.actions.documentUploadFailed({
                    classification,
                    error: e,
                }),
            );
        };
        const completionListener = async (): Promise<void> => {
            uploadTask = undefined;
            console.info("upload completed, waiting for approval from backend");
        };
        const path = `users/${uid}/configurations/${configurationId}/documents/${filename}`;

        const customMetadata = {
            configurationId,
            classification,
            userFilename,
        };
        staledTimeoutId = window.setTimeout(() => {
            return dispatch(configurationSlice.actions.documentUploadStaled({ classification }));
        }, 5000);

        dispatch(
            configurationSlice.actions.documentUploadStarted({
                classification,
                userFilename,
                mimeType,
                fileSize: size,
            }),
        );
        uploadTask = firebaseClient.uploadFile(
            path,
            file,
            mimeType,
            customMetadata,
            progressListener,
            errorListener,
            completionListener,
        );
    };
}

export const configurationSlice = createSlice({
    name: "configurations",
    initialState: initialConfigurationsState,
    reducers: {
        load: (state, { payload: confId }: PayloadAction<string>) => {
            state.currentConfigurationId = confId;
            if (confId === "new") {
                state.configurations["new"] = { ...newConfiguration };
            }
        },
        restoreNew: (state, { payload: stateToRestore }: PayloadAction<InitialConfiguration>) => {
            const restoredState = produce(newConfiguration, (restoredState) => {
                const buildingAddress = stateToRestore.buildingAddress;
                if (buildingAddress.type === "postal") {
                    restoredState.state.buildingAddress!.state = {
                        type: "postal",
                        city: {
                            value: buildingAddress.city,
                        },
                        zipCode: {
                            value: buildingAddress.zipCode,
                        },
                        street: {
                            value: buildingAddress.street,
                        },
                        houseNumber: {
                            value: buildingAddress.houseNumber,
                        },
                        district: buildingAddress.district
                            ? {
                                  value: buildingAddress.district,
                              }
                            : undefined,
                    };
                } else {
                    restoredState.state.buildingAddress!.state = {
                        type: "cadaster",
                        city: {
                            value: buildingAddress.city,
                        },
                        mark: {
                            value: buildingAddress.mark,
                        },
                        parcel: {
                            value: buildingAddress.parcel,
                        },
                        zipCode: {
                            value: buildingAddress.zipCode,
                        },
                        corridor: {
                            value: buildingAddress.corridor || null,
                        },
                        district: {
                            value: buildingAddress.district || null,
                        },
                        houseNumber: {
                            value: buildingAddress.houseNumber || null,
                        },
                        street: {
                            value: buildingAddress.street || null,
                        },
                    };
                }
            });
            state.configurationToRestore = restoredState;
        },
        removeNewAndClearSelection: (state) => {
            if (state.configurations.new) {
                delete state.configurations.new;
            }
            delete state.currentConfigurationId;
        },
        setBuildingAddress: (
            state,
            {
                payload: newAddress,
            }: PayloadAction<FormModel<ChangeNames.BUILDING_ADDRESS>["buildingAddress"]>,
        ) => {
            if (!state.currentConfigurationId) return;
            state.configurations[state.currentConfigurationId].state.buildingAddress = {
                default: false,
                relevant: true,
                state: {
                    ...newAddress,
                },
            };
        },
        productPartners: (
            state,
            { payload: partners }: PayloadAction<PartnerResponse["partners"]>,
        ) => {
            state.productPartners = partners;
        },
        overwrite: (state, { payload: configurations }: PayloadAction<Configuration[]>) => {
            const newConfig = state.configurations["new"];
            state.configurations = newConfig ? { new: newConfig } : {};
            configurations.forEach((config) => {
                if (!config.id) {
                    console.warn("a configuration slipt into state without its id being set!");
                    return;
                }
                // config.state.chargerChargingStations = {
                //     default: false,
                //     relevant: true,
                //     state: {
                //         value: [
                //             {
                //                 count: 1,
                //                 chargingPoints: [
                //                     {
                //                         value: {
                //                             amount: null,
                //                             precision: 0,
                //                             unit: "kVA",
                //                         },
                //                     },
                //                 ],
                //             },
                //         ],
                //     },
                // };
                state.configurations[config.id] = config;
            });
            if (state.configurationToRestore) {
                state.configurations["new"] = state.configurationToRestore;
                delete state.configurationToRestore;
            }
            state.initialized = true;
        },
        documentsOverwrite: (state, { payload: documents }: PayloadAction<DocumentCollection>) => {
            if (!state.currentConfigurationId) {
                console.error(
                    "no current configuration is set. dispatch 'load(confId) before using this action'",
                );
                return;
            }
            state.configurations[state.currentConfigurationId].documents = documents;
        },
        documentUploadStarted: (
            state,
            {
                payload: { classification, fileSize, mimeType, userFilename },
            }: PayloadAction<{
                classification: string;
                userFilename: string;
                mimeType: string;
                fileSize: number;
            }>,
        ) => {
            if (!state.currentConfigurationId) {
                console.error(
                    "no current configuration is set. dispatch 'load(confId) before using this action'",
                );
                return;
            }
            const docState =
                state.configurations[state.currentConfigurationId].documents[classification];
            if (docState.relevant) {
                docState.progress = 1;
                docState.files.push({
                    bucketId: "",
                    contentType: mimeType,
                    createdAt: moment().unix(),
                    extension: userFilename.substr(userFilename.lastIndexOf(".") + 1),
                    userFilename: userFilename,
                    size: fileSize,
                    objectId: "uploading",
                });
            }
        },
        documentUploadStaled: (
            state,
            {
                payload: { classification },
            }: PayloadAction<{
                classification: string;
            }>,
        ) => {
            if (!state.currentConfigurationId) {
                console.error(
                    "no current configuration is set. dispatch 'load(confId) before using this action'",
                );
                return;
            }
            const docState =
                state.configurations[state.currentConfigurationId].documents[classification];
            if (docState.relevant) {
                docState.progress = -1;
            }
        },
        documentUploadProgress: (
            state,
            {
                payload: { classification, progress },
            }: PayloadAction<{
                classification: string;
                progress: number;
            }>,
        ) => {
            if (!state.currentConfigurationId) {
                console.error(
                    "no current configuration is set. dispatch 'load(confId) before using this action'",
                );
                return;
            }
            const docState =
                state.configurations[state.currentConfigurationId].documents[classification];
            if (docState.relevant) {
                docState.progress = Math.max(1, progress);
            }
        },
        documentUploadFailed: (
            state,
            {
                payload: { classification, error },
            }: PayloadAction<{
                classification: string;
                error: Error;
            }>,
        ) => {
            if (!state.currentConfigurationId) {
                console.error(
                    "no current configuration is set. dispatch 'load(confId) before using this action'",
                );
                return;
            }
            const docState =
                state.configurations[state.currentConfigurationId].documents[classification];
            if (docState.relevant) {
                // user canceled upload
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((error as any).code === "storage/canceled") {
                    docState.files.pop();
                    docState.progress = undefined;
                } else {
                    docState.error = error;
                }
            }
        },
        setCreating: (state, { payload }: PayloadAction<boolean>) => {
            state.creating = payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(AuthActions.unauthenticated, (state) => ({
                ...initialConfigurationsState,
                configurationToRestore: state.configurationToRestore,
            }))
            .addCase(initialize.fulfilled, (state, { payload: configId }) => {
                if (!state.currentConfigurationId) {
                    console.error(
                        "no current configuration is set. dispatch 'load(confId) before using this action'",
                    );
                    return;
                }
                state.configurations[state.currentConfigurationId].id = configId;
            })
            .addCase(initialize.rejected, (state, { payload, error }) => {
                if (!state.currentConfigurationId) {
                    console.error(
                        "no current configuration is set. dispatch 'load(confId) before using this action'",
                    );
                    return;
                }
                state.configurations[state.currentConfigurationId].error =
                    (payload?.response?.data as any)?.message || error.message;
            })
            .addCase(change.fulfilled, (state) => {
                if (!state.currentConfigurationId) {
                    console.error(
                        "no current configuration is set. dispatch 'load(confId) before using this action'",
                    );
                    return;
                }
                state.configurations[state.currentConfigurationId].error = undefined;
            })
            .addCase(change.rejected, (state, { payload, error }) => {
                if (!state.currentConfigurationId) {
                    console.error(
                        "no current configuration is set. dispatch 'load(confId) before using this action'",
                    );
                    return;
                }
                state.configurations[state.currentConfigurationId].error =
                    (payload?.response?.data as any)?.message || error.message;
            })
            .addCase(order.fulfilled, (state) => {
                if (!state.currentConfigurationId) {
                    console.error(
                        "no current configuration is set. dispatch 'load(confId) before using this action'",
                    );
                    return;
                }
                state.configurations[state.currentConfigurationId].error = undefined;
            })
            .addCase(order.rejected, (state, { payload, error }) => {
                if (!state.currentConfigurationId) {
                    console.error(
                        "no current configuration is set. dispatch 'load(confId) before using this action'",
                    );
                    return;
                }
                state.configurations[state.currentConfigurationId].error =
                    (payload?.response?.data as any)?.message || error.message;
            });
    },
});

export const ConfigurationActions = {
    ...configurationSlice.actions,
    subscribeConfigurations,
    subscribeStatic,
    initialize,
    initializeOperation,
    change,
    order,
    remove,
    requestPartner,
    documentUpload,
    documentCancelUpload,
    documentRemove,
};
